import React, { useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useAuth } from '../contexts/AuthContext';
import { userNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2'
import AddCustomer from '../components/AddCustomer';
import PageHeader from '../components/PageHeader';

const CustomerList = () => {
    const { getAllCustomers, resetCredits, manageIsActiveCustomer, deleteCustomer, archiveCustomer } = useCustomer()
    const { restaurantId, auth, hasPermission } = useAuth();
    const { currency } = useCompanySetting()

    const canCreate = useMemo(() => hasPermission("users", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("users", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("users", "delete"), [hasPermission]);

    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [customerList, setCustomerList] = useState();
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [editCustomerId, setEditCustomerId] = useState(null);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showDeactivatedCustomers, setShowDeactivatedCustomers] = useState(false);

    const fetchCustomers = async () => {
        try {
            setIsLoading(true)
            const isActive = showDeactivatedCustomers ? 0 : 1
            let customers = await getAllCustomers(currentPage, rowsPerPage, sortField, sortOrder, filter, isActive);
            if (customers.length !== 0) {
                setCustomerList(customers.data)
                setTotalRecords(customers.totalCount)
            }
            setIsLoading(false)
        } catch (error) {
            console.error('Error fetching customers', error);
        }
    }

    useEffect(() => {
        if (restaurantId) {
            fetchCustomers()
        }
    }, [showDeactivatedCustomers, currentPage, rowsPerPage, sortField, sortOrder, restaurantId])

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchCustomers()
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const handleSubmit = async (e) => {
        e.preventDefault()
        fetchCustomers();
    };

    useEffect(() => {
        if (filter.trim() === "" && restaurantId) {
            fetchCustomers();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, filter, restaurantId]);

    const handleResetCredits = async () => {
        try {
            const customersToUpdate = selectedCustomers.map(customer => ({
                id: customer.id,
                credits: 0
            }));

            const response = await resetCredits(customersToUpdate);

            if (response.error === false) {
                setSelectedCustomers([]);
                fetchCustomers()
            }
        } catch (error) {
            console.error('Error resetting credits:', error);
        }
    };

    const handleIsActiveCustomer = async (is_active) => {
        try {
            const customersToUpdate = selectedCustomers.map(customer => ({
                id: customer.id,
                is_active: is_active
            }));

            const response = await manageIsActiveCustomer(customersToUpdate);

            if (response.error === false) {
                setSelectedCustomers([]);
                fetchCustomers()
            }
        } catch (error) {
            console.error('Error Deactivate Customers:', error);
        }
    };

    const handleShowDeactivatedCustomers = () => {
        setShowDeactivatedCustomers(!showDeactivatedCustomers);
    };

    const handleUpdate = (id) => {
        setEditMode(true);
        setEditCustomerId(id);
        setVisible(false);
    };

    const handleDelete = async (id) => {
        try {
            const confirmed = await confirmDelete('delete');

            if (confirmed) {
                await deleteCustomer(id);
                fetchCustomers();
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    };

    const handleArchive = async (id) => {
        try {
            const confirmed = await confirmDelete('archive');

            if (confirmed) {
                await archiveCustomer(id);
                fetchCustomers();
            }
        } catch (error) {
            console.error('Error during archive operation', error);
        }
    };

    const confirmDelete = async (label) => {
        return new Promise((resolve) => {
            confirmDialog({
                message: `Are you sure you want to ${label} this customer?`,
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    return (
        <Layout2 items={userNav.items} navTitle={userNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Customers'} />
                <div className='d-flex flex-row px-4 py-3 w-100 user-list-nav justify-content-between'>
                    {
                        canCreate && 
                        <button className='btn btn-success' onClick={() => setVisible(true)}>New Customer</button>
                    }
                    {/* <div className='d-flex'>
                        <button className='btn btn-success me-2'>Export Customer</button>
                        <button className='btn btn-success'>Import Customer</button>
                    </div> */}
                </div>
                <div className='px-4 py-4 d-flex'>
                    <ConfirmDialog />
                    {
                        (visible || (editMode && editCustomerId)) &&
                        (
                            <AddCustomer
                                id={editCustomerId}
                                customerVisible={visible}
                                editCustomerId={editCustomerId}
                                editMode={editMode}
                                setEditCustomerId={setEditCustomerId}
                                setCustomerVisible={setVisible}
                                setEditMode={setEditMode}
                                fetchCustomers={fetchCustomers}
                            />
                        )
                    }
                    {canUpdate && 
                        <>  
                            <button
                                className='btn btn-success me-2'
                                disabled={
                                    selectedCustomers.length === 0 ||
                                    selectedCustomers.filter(customer => customer.is_active === 1).length === 0
                                }
                                onClick={() => handleIsActiveCustomer(0)}
                            >
                                Deactivate
                            </button>
                            <button
                                className='btn btn-success me-2'
                                disabled={
                                    selectedCustomers.length === 0 ||
                                    selectedCustomers.filter(customer => customer.is_active === 0).length === 0
                                }
                                onClick={() => handleIsActiveCustomer(1)}
                            >
                                Activate
                            </button>
                            <button
                                className='btn btn-success me-3'
                                disabled={selectedCustomers.length === 0}
                                onClick={handleResetCredits}
                            >
                                Reset Credits
                            </button>
                        </>
                    }
                    <form onSubmit={handleSubmit}>
                        <span className="p-input-icon-left me-2">
                            <i className="pi pi-search" />
                            <InputText
                                placeholder="Search Customer"
                                type='search'
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                style={{ height: "38.48px" }}
                            />
                        </span>
                    </form>
                    <div className="d-flex align-items-center">
                        <Checkbox className='me-2' checked={showDeactivatedCustomers} onChange={handleShowDeactivatedCustomers} />
                        <label htmlFor="ingredient1" className='deativateCutomer'>Show Deactivate</label>
                    </div>
                </div>
                <div className='px-4 py-2'>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={customerList}
                            selectionMode="checkbox"
                            selection={selectedCustomers}
                            onSelectionChange={(e) => setSelectedCustomers(e.value)}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No customers found."
                            paginatorLeft={
                                <Dropdown value={rowsPerPage} options={[10, 25, 50]} onChange={(e) => setRowsPerPage(e.value)} />
                            }
                            selectOnEdit={false}
                        >
                            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                            <Column
                                field="id"
                                header="ID"
                                sortable
                                filterField="id"
                            />
                            {/* <Column
                                field="products"
                                header="Products"
                                sortable
                                filterField="products"
                            /> */}
                            <Column
                                field="firstName"
                                header="First Name"
                                body={(rowData) => (
                                    <div className="d-flex align-items-center">
                                        <span className='me-2'>{rowData.firstName}</span>
                                        {rowData.is_active == 0 && <Badge severity="danger" value="Deactive" />}
                                    </div>
                                )}
                                sortable
                                filterField="firstName"
                            />
                            <Column
                                field="lastName"
                                header="Last Name"
                                sortable
                                filterField="lastName"
                            />
                            <Column
                                field="email"
                                header="Email"
                                sortable
                                filterField="email"
                                body={(rowData) => (
                                    <div className="description" title={rowData.email}>
                                        {rowData.email}
                                    </div>
                                )}
                            />
                            <Column
                                field="companyName"
                                header="Company Name"
                                sortable
                                filterField="companyName"
                                body={(rowData) => (
                                    <div className="description" title={rowData.companyName}>
                                        {rowData.companyName}
                                    </div>
                                )}
                            />
                            <Column
                                field="VAT"
                                header="VAT"
                                sortable
                                filterField="VAT"
                                body={(rowData) => (
                                    <div className="description" title={rowData.VAT}>
                                        {rowData.VAT}
                                    </div>
                                )}
                            />
                            <Column
                                field="credits"
                                header="Balance"
                                sortable
                                filterField="credits"
                                body={(rowData) => (
                                    <div>
                                        {`${currency}${rowData.credits}`}
                                    </div>
                                )}
                            />
                            {(canUpdate || canDelete) && <Column
                                header="Action"
                                align="center"
                                body={(rowData) => (
                                    <div className='action'>
                                        {
                                            canUpdate &&
                                            <i className="pi pi-pencil pointerCursor" title='Edit' onClick={() => handleUpdate(rowData.id)} />
                                        }
                                        {
                                            canDelete && 
                                            <>
                                                <i className="pi pi-trash ms-2 pointerCursor" title='Delete' onClick={() => handleDelete(rowData.id)} />     
                                                <span title="Archive">
                                                    <Icon
                                                        icon="heroicons:archive-box-arrow-down"
                                                        className="ms-2 pointerCursor mb-1"
                                                        width="20"
                                                        height="20"
                                                        onClick={() => handleArchive(rowData.id)}
                                                    />
                                                </span>
                                            </>
                                        }
                                    </div>
                                )}
                            />}
                            {/* <Column
                                body={(rowData) => (
                                    <div>
                                        {rowData.credits != 0 && (
                                            <button className='btn btn-success' style={{ height: "35px" }}>Tabs</button>
                                        )}
                                    </div>
                                )}
                                align="center"
                            /> */}
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default CustomerList